import React, { useMemo } from 'react';
import type { FC } from 'react';
import cn from 'classnames';

import type { TCard } from './models';

export const PromoCardV2: FC<TCard> = ({
    Block,
    DataLayer,
    addOffset = false,
    analyticsClick,
    autoHeight = false,
    background,
    backgroundBlur,
    backgroundColor,
    backgroundPosition,
    backgroundSize,
    borderColor = '#d5d6dc',
    children,
    className,
    config,
    dataTestId,
    defaultHeight,
    defaultWidth,
    gradient,
    height,
    hoverEffect = false,
    hoverSlotFullWidth = false,
    hoverSlotPosition = 'bottomLeft',
    id,
    onClick,
    overflowHidden = false,
    styles,
    padding = config.padding,
    margin = config.margin,
    borderRadius = config.borderRadius,
    useNamedContainers,
    width,
    withBorder = false,
}) => {
    const { buttons, content, hover, title } = useNamedContainers(children);

    const currentWidth = width ?? defaultWidth;
    const currentHeight = height ?? defaultHeight;
    const backdropFilter = useMemo(
        () => (backgroundBlur ? `blur(${backgroundBlur})` : undefined),
        [backgroundBlur]
    );

    const hoverSlotStyles = {
        width: hoverSlotFullWidth ? `calc(100% - ${padding * 2}px)` : 'auto',
        top: hoverSlotPosition === 'bottomLeft' ? undefined : '50%',
        bottom: hoverSlotPosition === 'bottomLeft' ? padding : undefined,
        left: hoverSlotPosition === 'bottomLeft' ? padding : '50%',
    };

    return (
        <DataLayer data={analyticsClick} tag={null}>
            <Block
                className={cn(styles.card, styles.external, className, {
                    [styles.hoverEffect]: hover && hoverEffect,
                    [styles.cardMinHeight]: !autoHeight,
                    [styles.overflowHidden]: overflowHidden,
                })}
                dataTestId={dataTestId}
                fullHeight={!autoHeight}
                id={id?.toString()}
                notPadded
                onClick={onClick}
                style={{
                    backgroundImage: background ? `url(${background})` : gradient,
                    backgroundColor,
                    backgroundPosition,
                    backgroundSize,
                    backdropFilter,
                    border: withBorder ? `1px solid ${borderColor}` : 'none',
                    WebkitBackdropFilter: backdropFilter,
                    width: currentWidth,
                    height: autoHeight ? undefined : currentHeight,
                }}
                styler={{
                    padding: addOffset ? 0 : padding,
                    borderRadius,
                    ...margin,
                }}
            >
                <div className={cn(styles.content, { [styles.visible]: addOffset })}>
                    <div
                        className={cn({
                            [styles.contentContainer]: addOffset,
                            [styles.hasOffset]: addOffset,
                        })}
                    >
                        {title}
                        {content}
                    </div>
                    <div className={styles.buttonContainer}>{buttons}</div>
                    {hover && hoverEffect && (
                        <div
                            className={cn(styles.hoverSlot, {
                                [styles.centeredHoverSlot]: hoverSlotPosition === 'center',
                            })}
                            data-test-id="hover-slot"
                            style={hoverSlotStyles}
                        >
                            {hover}
                        </div>
                    )}
                </div>
            </Block>
        </DataLayer>
    );
};
